import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

export default function SkyBeeProjectPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          SkyBee: Homeland Defense Interceptor UCAV
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Project Manager)</span> — AIAA Undergraduate Team Aircraft Design Competition (Oct 2024 – May 2025)
        </div>
        <div className={styles.affiliations}>
          Aviation and Aerospace University, Bangladesh (AAUB)
        </div>
        <div>
          <span
            className={styles.badgeHighlight}
            style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#7c3aed', borderColor: 'rgba(139, 92, 246, 0.3)' }}
          >
            🥉 AIAA Student Conference VII, 2025 — 3rd Place (Team Category)
          </span>
        </div>
      </header>

      {/* Hero Image */}
      <img
        src="/assets/project/SkyBee Homeland Defense Interceptor/skybee.png"
        alt="SkyBee UCAV Render"
        className={styles.heroImage}
      />

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          SkyBee is a stealth-capable, remotely piloted Unmanned Combat Aerial Vehicle (UCAV)
          designed for the AIAA 2024-2025 Undergraduate Team Aircraft Design Competition.
          I led a 10-person team, supported by seven volunteers, in the conceptual design of
          SkyBee for Defensive Counter-Air (DCA) patrol, Point Defense Interception (PDI), and
          Escort missions, coordinating parallel workstreams across configuration, mass properties,
          structures, and stability & control. SkyBee achieves Mach 1.6 dash speed with a 30,984 lb
          maximum takeoff weight and 6.5-hour endurance, featuring a stealth-optimized airframe,
          internal weapons carriage, and a single F119-PW-100 low-bypass turbofan engine.
          Related structural analysis research received <strong>Third Place in the Team Category</strong> at the AIAA
          Student Conference VII, 2025.
        </p>
      </div>

      {/* Key Metrics */}
      <section>
        <h2 className={styles.sectionTitle}>Design Specifications</h2>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>Mach 1.6</div>
            <div className={styles.metricLabel}>Max Speed</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>30,984 lb</div>
            <div className={styles.metricLabel}>Max Takeoff Weight</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>6.5 hrs</div>
            <div className={styles.metricLabel}>Endurance</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>800 nm</div>
            <div className={styles.metricLabel}>Combat Radius</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>52.5 ft</div>
            <div className={styles.metricLabel}>Length</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>41.4 ft</div>
            <div className={styles.metricLabel}>Wingspan</div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section>
        <h2 className={styles.sectionTitle}>Design Gallery</h2>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/3-view-skybee.png"
              alt="SkyBee 3-View Drawing"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>3-View Technical Drawing</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/constraint-analysis-of-skybee.png"
              alt="Constraint Analysis"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Constraint Analysis (Design Point: 63.4, 1.13)</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/internal-layout-of-skybee.png"
              alt="Internal Layout"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Internal Layout & Systems Integration</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/v-n_diagram-of-skybee-at-sea-level.png"
              alt="V-n Diagram"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>V-n Diagram at Sea Level</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/skybee-structure.png"
              alt="Structural Model"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Structural Framework</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/Mission Profile CAP.jpg"
              alt="DCA Mission Profile"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Defensive Counter-Air Patrol Mission</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/Mission Profile PDI.jpg"
              alt="PDI Mission Profile"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Point Defense Intercept Mission</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/Mission Profile Escort.jpg"
              alt="Escort Mission Profile"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Intercept / Escort Mission</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img
              src="/assets/project/SkyBee Homeland Defense Interceptor/team-member-skybee.png"
              alt="Team Members"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>Frontier Alliance Team</figcaption>
          </figure>
        </div>
      </section>

      {/* Design Report Link */}
      <section>
        <h2 className={styles.sectionTitle}>Full Design Report</h2>
        <p className={styles.paragraph}>
          The complete 100-page detailed design report covers conceptual design, sizing analysis,
          aerodynamics, propulsion, structures, stability & control, avionics, cost analysis,
          and mission performance for all three design missions.
        </p>
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <a
            href="https://shorturl.at/glSOb"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnPrimary}
          >
            📄 View Full Design Report
          </a>
        </div>
      </section>

      {/* Published Papers */}
      <section>
        <h2 className={styles.sectionTitle}>Published Papers</h2>
        <div className={styles.pubList}>
          <div className={styles.pubItem}>
            <p className={styles.pubCitation}>
              <strong>1.</strong> "Aerodynamic and Performance Analysis of a Supersonic Next-Generation UCAV Using Hybrid Methodology" —{' '}
              <a href="https://lnkd.in/eVeXdFUB" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 700 }}>View Paper</a>
            </p>
          </div>
          <div className={styles.pubItem}>
            <p className={styles.pubCitation}>
              <strong>2.</strong> "Static Structural Analysis and Material Selection of a Supersonic Fighter Aircraft's Wing" —{' '}
              <a href="https://doi.org/10.2514/6.2025-106584" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 700 }}>View Paper (DOI: 10.2514/6.2025-106584)</a>
            </p>
          </div>
          <div className={styles.pubItem}>
            <p className={styles.pubCitation}>
              <strong>3.</strong> "Propulsion, Landing Gear, Auxiliary Systems, and Avionics Integration in a Newly Designed Unmanned Fighter Aircraft" —{' '}
              <a href="https://lnkd.in/eAr_z3Mx" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 700 }}>View Paper</a>
            </p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section>
        <h2 className={styles.sectionTitle}>Team & Advisors</h2>
        <p className={styles.paragraph}>
          <strong>Team Lead:</strong> Hemal Ishak ·{' '}
          <strong>Project Manager:</strong> Jolok Banarjee ·{' '}
          <strong>Aerodynamics & Performance:</strong> Ahnaf Ahmed Nuhash ·{' '}
          <strong>Stability & Control:</strong> MD Ahad Israq ·{' '}
          <strong>Propulsion:</strong> Md. Saiful Islam ·{' '}
          <strong>Mass Properties:</strong> Mahi Uddin Ahmaad ·{' '}
          <strong>Cost Analysis:</strong> A M Saadnan Tahsin ·{' '}
          <strong>Landing Gear:</strong> Ashiquzzaman Sadi ·{' '}
          <strong>Auxiliary Systems:</strong> Md. Khairul Habib ·{' '}
          <strong>CAD:</strong> Md Abuzer Gifari Jubaer
        </p>
        <p className={styles.paragraph} style={{ marginTop: '0.5rem' }}>
          <strong>Faculty Advisor:</strong> Md. Toufiq Islam Noor ·{' '}
          <strong>Project Advisor:</strong> Air Cdre Dr. Saifur Rahman Bakaul
        </p>
      </section>

      {/* Volunteers */}
      <section>
        <h2 className={styles.sectionTitle}>Volunteers</h2>
        <p className={styles.paragraph}>
          A huge thank you to our seven amazing volunteers for helping us collect resources,
          sort out documentation, and support the team throughout the competition:
        </p>
        <p className={styles.paragraph} style={{ marginTop: '0.5rem' }}>
          <strong>Abrar Ur Rahman</strong> · <strong>Mahathir Muhammad</strong> · <strong>Payel Saha</strong> · <strong>AL Mehedi Hasan Leon</strong> ·{' '}
          <strong>Fabia Ziban Shaily</strong> · <strong>Mohammad Raihan Samee</strong> · <strong>Anika Kabir</strong>
        </p>
      </section>
    </div>
  );
}